import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Megaphone, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function OwnerPromotions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newPromo, setNewPromo] = useState({ title: "", title_el: "", description: "", description_el: "", discount_text: "", discount_text_el: "", start_date: "", end_date: "", start_time: "", end_time: "" });
  const [adding, setAdding] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/owner/promotions`)
      .then(res => setPromos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newPromo.title || !newPromo.start_date || !newPromo.end_date) {
      toast.error(t("owner_promotions:required_error"));
      return;
    }
    setAdding(true);
    try {
      const payload = { ...newPromo };
      if (!payload.start_time) delete payload.start_time;
      if (!payload.end_time) delete payload.end_time;
      const res = await axios.post(`${API}/owner/promotions`, payload);
      setPromos([res.data, ...promos]);
      setNewPromo({ title: "", title_el: "", description: "", description_el: "", discount_text: "", discount_text_el: "", start_date: "", end_date: "", start_time: "", end_time: "" });
      setShowAdd(false);
      toast.success(t("owner_promotions:created"));
    } catch (err) { toast.error(err.response?.data?.detail || t("owner_promotions:create_failed")); }
    finally { setAdding(false); }
  };

  const handleUpdate = async () => {
    if (!editingPromo) return;
    try {
      const payload = { title: editingPromo.title, title_el: editingPromo.title_el || "", description: editingPromo.description, description_el: editingPromo.description_el || "", discount_text: editingPromo.discount_text, discount_text_el: editingPromo.discount_text_el || "", start_date: editingPromo.start_date, end_date: editingPromo.end_date, is_active: editingPromo.is_active };
      if (editingPromo.start_time) payload.start_time = editingPromo.start_time;
      if (editingPromo.end_time) payload.end_time = editingPromo.end_time;
      const res = await axios.put(`${API}/owner/promotions/${editingPromo.promo_id}`, payload);
      setPromos(promos.map(p => p.promo_id === editingPromo.promo_id ? res.data : p));
      setEditingPromo(null);
      toast.success(t("owner_promotions:updated"));
    } catch { toast.error(t("owner_promotions:update_failed")); }
  };

  const handleDelete = async (promoId) => {
    try {
      await axios.delete(`${API}/owner/promotions/${promoId}`);
      setPromos(promos.filter(p => p.promo_id !== promoId));
      toast.success(t("owner_promotions:deleted"));
    } catch { toast.error(t("owner_promotions:delete_failed")); }
  };

  const isExpired = (endDate) => new Date(endDate) < new Date();
  const isActive = (promo) => promo.is_active && !isExpired(promo.end_date);

  const localTitle = (p) => (lang === "el" && p.title_el) || p.title;
  const localDesc = (p) => (lang === "el" && p.description_el) || p.description;
  const localDiscount = (p) => (lang === "el" && p.discount_text_el) || p.discount_text;

  if (loading) {
    return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-20 rounded-xl" />)}</div>;
  }

  return (
    <div data-testid="owner-promotions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" /> {t("owner_promotions:title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("owner_promotions:subtitle")}</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} data-testid="owner-add-promo-btn">
          <Plus className="h-4 w-4 mr-2" /> {t("owner_promotions:create_offer")}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6 space-y-4" data-testid="owner-add-promo-form">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("owner_promotions:field_title_en")} *</Label><Input value={newPromo.title} onChange={e => setNewPromo({ ...newPromo, title: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_title")} data-testid="owner-promo-title" /></div>
            <div><Label>{t("owner_promotions:field_title_el")}</Label><Input value={newPromo.title_el} onChange={e => setNewPromo({ ...newPromo, title_el: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_title")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("owner_promotions:field_description_en")}</Label><Input value={newPromo.description} onChange={e => setNewPromo({ ...newPromo, description: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_desc_en")} /></div>
            <div><Label>{t("owner_promotions:field_description_el")}</Label><Input value={newPromo.description_el} onChange={e => setNewPromo({ ...newPromo, description_el: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_desc_el")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("owner_promotions:field_discount_en")}</Label><Input value={newPromo.discount_text} onChange={e => setNewPromo({ ...newPromo, discount_text: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_discount_en")} data-testid="owner-promo-discount" /></div>
            <div><Label>{t("owner_promotions:field_discount_el")}</Label><Input value={newPromo.discount_text_el} onChange={e => setNewPromo({ ...newPromo, discount_text_el: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_discount_el")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("owner_promotions:field_start_date")} *</Label><Input type="date" value={newPromo.start_date} onChange={e => setNewPromo({ ...newPromo, start_date: e.target.value })} className="mt-1" data-testid="owner-promo-start" /></div>
            <div><Label>{t("owner_promotions:field_end_date")} *</Label><Input type="date" value={newPromo.end_date} onChange={e => setNewPromo({ ...newPromo, end_date: e.target.value })} className="mt-1" data-testid="owner-promo-end" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("owner_promotions:field_daily_from")}</Label><Input type="time" value={newPromo.start_time} onChange={e => setNewPromo({ ...newPromo, start_time: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_time_start")} /></div>
            <div><Label>{t("owner_promotions:field_daily_until")}</Label><Input type="time" value={newPromo.end_time} onChange={e => setNewPromo({ ...newPromo, end_time: e.target.value })} className="mt-1" placeholder={t("owner_promotions:placeholder_time_end")} /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={adding} data-testid="owner-promo-submit">{adding ? t("owner_promotions:creating") : t("owner_promotions:create_offer")}</Button>
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t("owner_promotions:cancel")}</Button>
          </div>
        </div>
      )}

      {promos.length === 0 && !showAdd ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium text-foreground mb-1">{t("owner_promotions:no_offers_yet")}</h3>
          <p className="text-sm text-muted-foreground">{t("owner_promotions:no_offers_sub")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(promo => (
            <div key={promo.promo_id} className={`bg-card rounded-xl border p-5 ${isActive(promo) ? "border-amber-200" : "border-border opacity-70"}`} data-testid={`owner-promo-${promo.promo_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-foreground">{localTitle(promo)}</h3>
                    {localDiscount(promo) && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">{localDiscount(promo)}</span>}
                    {isActive(promo) ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t("owner_promotions:live")}</span>
                    ) : isExpired(promo.end_date) ? (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t("owner_promotions:expired")}</span>
                    ) : (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{t("owner_promotions:paused")}</span>
                    )}
                  </div>
                  {localDesc(promo) && <p className="text-sm text-muted-foreground mt-1">{localDesc(promo)}</p>}
                  <p className="text-xs text-muted-foreground mt-2">
                    {promo.start_date} {t("owner_promotions:date_to")} {promo.end_date}
                    {promo.start_time && promo.end_time && ` \u2022 ${t("owner_promotions:daily_label")} ${promo.start_time} - ${promo.end_time}`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditingPromo({ ...promo })} data-testid={`owner-promo-edit-${promo.promo_id}`}><Edit className="h-4 w-4 text-blue-500" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(promo.promo_id)} data-testid={`owner-promo-delete-${promo.promo_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {editingPromo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingPromo(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="owner-edit-promo-dialog">
            <h3 className="text-lg font-medium">{t("owner_promotions:edit_offer")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("owner_promotions:field_title_en")} *</Label><Input value={editingPromo.title} onChange={e => setEditingPromo({ ...editingPromo, title: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("owner_promotions:field_title_el")}</Label><Input value={editingPromo.title_el || ""} onChange={e => setEditingPromo({ ...editingPromo, title_el: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("owner_promotions:field_description_en")}</Label><Input value={editingPromo.description || ""} onChange={e => setEditingPromo({ ...editingPromo, description: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("owner_promotions:field_description_el")}</Label><Input value={editingPromo.description_el || ""} onChange={e => setEditingPromo({ ...editingPromo, description_el: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("owner_promotions:field_discount_en")}</Label><Input value={editingPromo.discount_text || ""} onChange={e => setEditingPromo({ ...editingPromo, discount_text: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("owner_promotions:field_discount_el")}</Label><Input value={editingPromo.discount_text_el || ""} onChange={e => setEditingPromo({ ...editingPromo, discount_text_el: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("owner_promotions:field_start_date")}</Label><Input type="date" value={editingPromo.start_date} onChange={e => setEditingPromo({ ...editingPromo, start_date: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("owner_promotions:field_end_date")}</Label><Input type="date" value={editingPromo.end_date} onChange={e => setEditingPromo({ ...editingPromo, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("owner_promotions:field_time_from")}</Label><Input type="time" value={editingPromo.start_time || ""} onChange={e => setEditingPromo({ ...editingPromo, start_time: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("owner_promotions:field_time_until")}</Label><Input type="time" value={editingPromo.end_time || ""} onChange={e => setEditingPromo({ ...editingPromo, end_time: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editingPromo.is_active} onChange={e => setEditingPromo({ ...editingPromo, is_active: e.target.checked })} className="rounded" />
              <span className="text-sm">{t("owner_promotions:active")}</span>
            </label>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditingPromo(null)}>{t("owner_promotions:cancel")}</Button>
              <Button size="sm" onClick={handleUpdate} data-testid="owner-edit-promo-save">{t("owner_promotions:save")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
